// google-analytics-services.ts
// Google Analytics 4 (GA4) integration for web platform only

import { Capacitor } from "@capacitor/core";

// Replace with your actual GA4 Measurement ID
const GA_MEASUREMENT_ID = 'G-7S4QRKB8WH'; // Replace with your GA4 Measurement ID

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export const initGoogleAnalytics = () => {
  // Only initialize for web platform
  if (Capacitor.isNativePlatform()) {
    console.log('📱 Native platform detected - skipping Google Analytics');
    return;
  }

  if (typeof window === 'undefined') {
    console.log('🌐 Server-side rendering - skipping Google Analytics');
    return;
  }

  // Check if already initialized
  if (typeof window.gtag === 'function' && window.dataLayer && window.dataLayer.length > 0) {
    console.log('📊 Google Analytics already initialized');
    return;
  }

  try {
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Set initial timestamp
    window.gtag('js', new Date());

    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    // Add script to head
    document.head.appendChild(script);

    // Initialize GA4
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });

    console.log('✅ Google Analytics initialized for web platform');
  } catch (error) {
    console.error('❌ Failed to initialize Google Analytics:', error);
  }
};

export const trackGAEvent = (eventName: string, parameters?: Record<string, any>) => {
  // Only track for web platform
  if (Capacitor.isNativePlatform()) {
    return;
  }

  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  try {
    window.gtag('event', eventName, parameters);
  } catch (error) {
    console.error('❌ Failed to track GA event:', error);
  }
};

export const trackGAPageView = (pagePath: string, pageTitle?: string) => {
  // Only track for web platform
  if (Capacitor.isNativePlatform()) {
    return;
  }

  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  try {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: pageTitle || document.title,
      page_location: window.location.origin + pagePath,
    });
  } catch (error) {
    console.error('❌ Failed to track GA page view:', error);
  }
};
